import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import { Box, Button, Modal, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import loader from 'src/components/LoaderImg/loader.gif';
import { singleSignatureWorkflow } from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import DailyNoteSignature from 'src/pages/therapist-dashboard/therapist-document/daily-note-signature';
import { resetEsignAction, submitEsignAction } from 'src/redux/actions/dailySessionNotes';
import {
  editSignDocAction,
  resetDashboardDocumentESign,
  resetNewDocumentSignAction,
  resetSubmissionEventAction,
  undoDocumentSignAction
} from 'src/redux/actions/documentationAction';
import { getAccessToken } from 'src/routes/AuthContext';
import { modalStyle } from 'src/utils/common-properties';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import SelectSuperVisor from '../document-management/select-supervisor';
import SignDocementModel from '../document-management/signature-document-popup';
import { Seo } from '../seo';
import SucessModal from '../sucess-modal';
import UndoSignConformation from './undo-Sign-conformation';

type Props = {
  dailySessionNotesInfoResponse?: any;
  selectedPatient?: any;
  setOpen: any;
  selectedDate: any;
  setOpenEditNotes: any;
};

function ViewSOAPDocument({
  dailySessionNotesInfoResponse,
  selectedPatient,
  setOpen,
  selectedDate,
  setOpenEditNotes,
}: Props) {
  const dispatch = useDispatch();
  const providerId = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const dailySessionNotesInfo = useSelector((state: any) => state?.getDailySessionNotes?.response);
  const [discipline] = useCurrentUser(['discipline']);
  const [esignConfirmModal, setEsignConfirmModal] = useState({
    success: false,
    warning: false,
  });
  const submitEsignResponse = useSelector((state: any) => state.submitEsignReducer);

  const [isSingleSignatureWorkflow] = useFeatureToggleWhenReady(
    singleSignatureWorkflow,
    'ENTERPRISE'
  );

  const [undoSign, setUndoSign] = useState(false);
  const [iframeRef, setIframeRef] = useState<any | null>(null);
  const [openNewSignModel, setOpenNewSignModel] = useState(false);
  const [isDocSigned, setIsDocSigned] = useState({
    isSuccessful: false,
    pdfUrl: '',
  });


  const updateIFrameRef = useCallback((node: any) => {
    setIframeRef(node);
  }, []);

  useEffect(() => {
    const iframeElement = iframeRef;

    if (iframeElement) {
      const interval = setInterval(async () => {
        const response = await getAccessToken();
      }, 15000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [iframeRef]);

  const selectedPatientName = `${selectedPatient?.firstName} ${selectedPatient?.lastName}`;
  const callGetNoteTemplate = () => {
    if (
      (selectedPatient?.name
        ? dailySessionNotesInfo?.dailySessionNote?.status
        : dailySessionNotesInfoResponse?.status) === 'SIGNED'
    ) {
      setUndoSign(true);
      return;
    }
    setOpenEditNotes(true);
  };

  const [openESign, setOpenEsign] = useState(false);

  const submitForEsign = () => {
    dispatch(resetNewDocumentSignAction());
    dispatch(
      submitEsignAction({
        uuid: selectedPatient?.name
          ? dailySessionNotesInfo?.dailySessionNote?.uuid
          : dailySessionNotesInfoResponse?.dailySessionNote?.uuid,
        formId: selectedPatient?.name
          ? dailySessionNotesInfo?.prefillKey?.split('/')[3]
          : dailySessionNotesInfoResponse?.prefillKey?.split('/')[3],
        therapistId: providerId,
        type: discipline,
        isFeatureToggle: isSingleSignatureWorkflow

      })
    );
  };

  const [keys, setKeys] = useState({
    documentId: '',
    embedKey: '',
  });

  const signatureTemplateResponse = useSelector((state: any) => state.getSignatureTemplateReducer);

  useEffect(() => {
    if (
      !signatureTemplateResponse.isGetSignatureTemplate &&
      signatureTemplateResponse.response === null
    ) {
      setOpenEsign(false);
      return;
    }
    setKeys({
      documentId: signatureTemplateResponse.response?.documentId,
      embedKey: signatureTemplateResponse.response?.embedKey,
    });
    setOpenEsign(true);
  }, [signatureTemplateResponse]);

  const closeConfirmationModal = () => {
    setEsignConfirmModal({
      success: false,
      warning: false,
    });
    dispatch(resetEsignAction());
  };

  const checkAddSupervisorResponse = useSelector((state: any) => state.callSubmitEventReducer);
  const [triggerSelectSupervisor, setTriggerSupervisor] = useState(false);
  const closeSupervisorModal = () => {
    dispatch(resetEsignAction());
    setTriggerSupervisor(false);
  };

  useEffect(() => {
    if (!submitEsignResponse.isSubmitted && submitEsignResponse.response === null) return;
    if (!submitEsignResponse.isSubmitted && submitEsignResponse.response !== null) {
      if (
        submitEsignResponse.response?.message &&
        submitEsignResponse.response?.message?.includes('SUPERVISOR_REQUIRED')
      ) {
        setTriggerSupervisor(true);
        return;
      } else return;
    }
    if (!submitEsignResponse.isSubmitted && submitEsignResponse.response !== null) {
      setEsignConfirmModal({
        success: false,
        warning: true,
      });
    }
    if (submitEsignResponse.isSubmitted && submitEsignResponse.response !== null && isSingleSignatureWorkflow) {
      setOpenNewSignModel(true)
      dispatch(resetEsignAction());
    }
  }, [submitEsignResponse]);

  useEffect(() => {
    if (!checkAddSupervisorResponse.isEventCalled && checkAddSupervisorResponse.response === null)
      return;
    dispatch(resetSubmissionEventAction());
    submitForEsign();
  }, [checkAddSupervisorResponse]);

  const handleBack = () => {
    dispatch(resetDashboardDocumentESign());
    setOpen(false);
  };

  const checkIsUndoSignWhileEdit = useSelector((state: any) => state.editSignDocumentReducer);
  const undoDocumentSignResponse = useSelector((state: any) => state.undoDocumentSignReducer);


  useEffect(() => {
    if (checkIsUndoSignWhileEdit?.isDocEdited || undoDocumentSignResponse?.isSuccess) {
      setUndoSign(false);
    }
  }, [checkIsUndoSignWhileEdit, undoDocumentSignResponse]);

  const confirmUndoSing = () => {
    if(!isSingleSignatureWorkflow || selectedPatient.isReformSignature === false) {
      dispatch(
        editSignDocAction(dailySessionNotesInfoResponse?.response?.formSubmissionId, {
          submissionId: dailySessionNotesInfoResponse?.response?.dailySessionNote?.submissionId,
          isForSupervisor: selectedPatient?.requiresSupervisorForDailySessionNote,
          formType: 'DAILY_SOAP_NOTE',
        })
      );
    } else {
      dispatch(
        undoDocumentSignAction({
          formSubmissionUuid: dailySessionNotesInfoResponse?.response?.formSubmissionId,
          documentType: 'DAILY_SOAP_NOTE',
        })
      );
    }
  };

  const handleCloseNewSignModel = () => {
    setOpenNewSignModel(false);
  };

  return (
    <>
      <Seo title="Patient: Session Notes" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
        }}
      >
        <Stack
          justifyContent="space-between"
          flexDirection="row"
          alignItems="center"
        >
          {selectedPatient ? (
            <Typography
              sx={{ fontSize: '18px !important' }}
              variant="h6"
            >
              {selectedPatientName
                ? selectedPatientName?.endsWith("'s")
                  ? selectedPatientName
                  : `${selectedPatientName}'s`
                : ''}{' '}
              notes
            </Typography>
          ) : (
            <Typography
              sx={{ fontSize: '18px !important' }}
              variant="h6"
            >
              No Records Found
            </Typography>
          )}

          <Stack
            flexDirection="row"
            gap="1rem"
            sx={{ height: 40 }}
          >
            {!openESign ? (
              <Button
                color="primary"
                disabled={new Date(selectedDate) > new Date()}
                variant="contained"
                onClick={callGetNoteTemplate}
              >
                Edit
              </Button>
            ) : null}
            <Button
              color="primary"
              variant="outlined"
              startIcon={<ArticleOutlinedIcon />}
              onClick={() => setOpen((prev: any) => !prev)}
              sx={{
                whiteSpace: 'nowrap',
                // width: 145,
                overflow: 'hidden',
              }}
            >
              Therapy Info
            </Button>
            {openESign ? (
              <Button
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
            ) : (selectedPatient?.name
                ? dailySessionNotesInfo?.dailySessionNote?.status
                : dailySessionNotesInfoResponse?.status) === 'FINALIZED' ? (
              <Button
                color="primary"
                variant="contained"
                startIcon={
                  submitEsignResponse.loading || signatureTemplateResponse?.loader ? (
                    <img
                      src={loader}
                      style={{ height: 30 }}
                      alt='Daily soap note'
                    />
                  ) : (
                    <TaskOutlinedIcon />
                  )
                }
                onClick={submitForEsign}
                sx={{
                  whiteSpace: 'nowrap',
                  width: 145,
                  overflow: 'hidden',
                }}
                disabled={submitEsignResponse.loading || signatureTemplateResponse?.loader}
              >
                Sign Document
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {!openESign &&
          (dailySessionNotesInfoResponse?.loading ? (
            <img
              src={loader}
              alt="loading"
              style={{ margin: 'auto', display: 'block', marginTop: '10%' }}
            />
          ) : (
            <Box
              sx={{ height: '100%' }}
              py={3}
            >
              <iframe
                src={dailySessionNotesInfoResponse?.response?.pdfUrl}
                width={'100%'}
                ref={updateIFrameRef}
                height={'100%'}
                style={{ border: 'none' }}
                title='daily session note'
              />
            </Box>
          ))}

        {openESign ? <DailyNoteSignature formSubmissionUuid={dailySessionNotesInfoResponse?.response?.formSubmissionId} keys={keys} /> : null}
      </Box>

      <Modal open={esignConfirmModal.warning}>
        <SucessModal
          close={closeConfirmationModal}
          isSucess={esignConfirmModal.success}
          title={' Failed E-Sign'}
          message={
            esignConfirmModal.success
              ? 'e-Sign is submitted successfully for this SOAP note'
              : submitEsignResponse.response?.message
          }
        />
      </Modal>
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={() => setUndoSign(false)}
        aria-labelledby="customized-dialog-title"
        open={undoSign}
      >
        <UndoSignConformation
          close={() => setUndoSign(false)}
          confirm={confirmUndoSing}
        />
      </BootstrapDialog>

      {/* For selecting a supervisor -> dummy data*/}
      <Modal
        style={modalStyle}
        open={triggerSelectSupervisor}
        onClose={closeSupervisorModal}
      >
        <div>
          <SelectSuperVisor
            activeTab={typeof discipline === 'string' ? discipline : ''}
            close={closeSupervisorModal}
            selectedDocument={dailySessionNotesInfoResponse?.dailySessionNote}
            role={''}
            source={'FROM_DSN'}
            DSNId={submitEsignResponse.response?.message?.split(':')[1]?.trim()}
          />
        </div>
      </Modal>

      <BootstrapDialog
        PaperProps={{ style: { width: '600px' } }}
        onClose={handleCloseNewSignModel}
        aria-labelledby="customized-dialog-title"
        open={openNewSignModel}
      >
        <SignDocementModel
          onClose={handleCloseNewSignModel}
          setIsDocSigned={setIsDocSigned}
          formSubmissionUuid={dailySessionNotesInfoResponse?.response?.formSubmissionId}
          isSupervisor={false}
          docType={'DAILY_SOAP_NOTE'}
          open
        />
      </BootstrapDialog>
    </>
  );
}

export default ViewSOAPDocument;
